import { DecimalPipe } from '@angular/common';
import { Component, Inject, LOCALE_ID, OnInit } from '@angular/core';
import { ITransactionHistoryItem, KeyValue, TransactionType } from '@carm/_core';
import { AgGridConfig } from '@carm/shared/components/table/table-ag-grid/models/table-actions-model';
import { RoutesConst } from '@carm/shared/constants/const-routes';
import * as dayjs from 'dayjs';
import { last, takeUntil } from 'rxjs/operators';
import { standardCarmMoneyFormat } from '@carm/_utils/utility-functions';
import { MetadataService } from '@carm/_core/metadata/services';
import { TransactionHistoryService } from '@carm/_core/financial/services/transaction-history.service';
import { combineLatest } from 'rxjs';
import { defaultCarmCurrency, minStartDate } from '@carm/shared/constants/const-carm';
import { CarmRouter } from '@carm/_core/carm-router/services/carm-router.service';
import { BaseComponent } from '@carm/_utils/base.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'carm-recent-transactions-widget',
  templateUrl: './recent-transactions.component.html',
  styleUrls: ['./recent-transactions.component.scss'],
})
export class RecentTransactionsWidgetComponent extends BaseComponent
  implements OnInit {
  private maxTransactionsPerWidget = 5;
  private sortField = 'TRANSACTION_DATE_DESC';

  public viewAllUrl: string;
  public agGridConfig: AgGridConfig;
  public transactions: ITransactionHistoryItem[];
  public currency: string;
  public minDate: Date;
  public maxDate: Date;
  public loading = false;
  private statuses: KeyValue[] = [];

  private txHistoryRes =
    'application_features.financialInformation.sections.transactionHistory.';

  constructor(
    private transactionHistoryService: TransactionHistoryService,
    protected metadataService: MetadataService,
    private decimalPipe: DecimalPipe,
    private carmRouter: CarmRouter,
    private translateService: TranslateService,
    @Inject(LOCALE_ID) public currentLocal: string
  ) {
    super();

    this.viewAllUrl = RoutesConst.Financial_TransactionHistory;
  }

  ngOnInit(): void {
    this.metadataService.getClearingStatuses().pipe(takeUntil(this.destroy$)).subscribe(statuses => {
      this.statuses = statuses;
    });

    this.loading = true;

    this.minDate = minStartDate;
    this.maxDate = dayjs().toDate();

    this.currency = defaultCarmCurrency;

    this.buildAgGridConfig();

    combineLatest([
      this.carmRouter.bn9$,
      this.carmRouter.bn15$,
    ])
    .pipe(takeUntil(this.destroy$))
    .subscribe(([
      bn9, programId
    ]) => {
      this.refreshTable(bn9, programId);
    }, () => {
      this.loading = false;
    });
  }

  private buildAgGridConfig() {

    this.agGridConfig = {
      rowModelType: 'clientSide',
      hideQuickFilter: true,
      columnDefs: [
        {
          headerName: this.translateService.instant(
            `${this.txHistoryRes}table.transactionDate`
          ),
          field: 'transactionDate',
          sortable: false,
          filter: false,
          resizable: true,
          valueFormatter: p => p.value?.formatDate(),
        },
        {
          headerName: this.translateService.instant(
            `${this.txHistoryRes}table.description`
          ),
          field: 'description',
          sortable: false,
          filter: false,
          resizable: true,
        },
        {
          headerName: this.translateService.instant(
            `${this.txHistoryRes}table.amount`,
            { currency: this.currency }
          ),
          field: 'amount',
          sortable: false,
          filter: false,
          resizable: true,
          type: 'numericColumn',
          valueFormatter: p =>
          standardCarmMoneyFormat(
            this.decimalPipe,
            p.value,
            null,
            this.currentLocal
          )
        },
        {
          headerName: this.translateService.instant(
            `${this.txHistoryRes}table.status`
          ),
          field: 'status',
          sortable: false,
          filter: false,
          resizable: true,
          valueFormatter: p => this.convertStatus(p.value),
        },
      ],
    };
  }

  private refreshTable(bn9: string, programId: string): void {
    this.loading = true;
    this.transactionHistoryService
      .getFinancialTransactionHistoryByBusinessAndProgram(
        bn9,
        programId,
        1,
        this.maxTransactionsPerWidget,
        this.minDate,
        this.maxDate,
        TransactionType.FINANCIAL,
        this.sortField
      )
      .pipe(takeUntil(this.destroy$) ,last())
      .subscribe(response => {
        this.transactions = response.transactions;
        this.loading = false;
      }, () => {
        this.loading = false;
      });
  }

  private convertStatus(status: string): string {
    this.statuses?.forEach(statusMeta => {
      if (statusMeta?.key === status)
        status =  statusMeta.value;
    })
    return status;
  }
}
